import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, CreditCard, Building } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useJobs } from '@/hooks/useJobs';
import { usePaymentSetupCoordination } from '@/hooks/usePaymentSetupCoordination';
import { useStripeConnect } from '@/hooks/useStripeConnect';
import { usePaymentSetup } from '@/hooks/usePaymentSetup';
import { useAuth } from '@/lib/auth';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { PaymentMethodSelector } from '@/components/payments/PaymentMethodSelector';
import { useCustomerPaymentMethods } from '@/hooks/useCustomerPaymentMethods';
import { supabase } from '@/integrations/supabase/client';
import { usePaymentProcessingEnabled } from '@/hooks/usePaymentProcessingEnabled';

export default function PaymentSetupPage() {
  const params = useParams();
  const jobId = params.jobId as string;
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { jobs, loading: jobLoading } = useJobs({});
  const job = jobs.find(j => j.public_id === jobId || j.id === jobId);

  // Call all hooks unconditionally - pass safe defaults when job not loaded
  const { paymentStatus, loading, userRole } = usePaymentSetupCoordination(
    job?.id || '',  // Empty string when job not loaded (will be handled by hook validation)
    job?.public_id || jobId,
    job?.status  // Pass job status to enable initialization healing path
  );

  const {
    stripeAccount,
    isConnected: vendorConnected,
    isPending: vendorPending,
    createConnectAccount,
    refreshAccountStatus,
  } = useStripeConnect(
    paymentStatus?.vendor_org_id || ''
  );

  const { createPaymentSetup } = usePaymentSetup(job?.id || '');
  
  // Payment method selection state for clients
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>();
  // Only fetch payment methods for clients - vendors don't have access to client payment methods
  const isClient = userRole === 'client';
  const { paymentMethods } = useCustomerPaymentMethods(
    isClient ? (paymentStatus?.client_org_id || '') : ''
  );

  const { paymentProcessingEnabled, isLoading: flagLoading } = usePaymentProcessingEnabled();

  // Load saved payment method selection
  useEffect(() => {
    const loadSavedPaymentMethod = async () => {
      if (!job?.id || !paymentStatus?.client_setup_complete) return;
      
      const { data } = await supabase
        .from('payment_setup_status')
        .select('stripe_payment_method_id')
        .eq('job_id', job.id)
        .single();
      
      if (data?.stripe_payment_method_id) {
        setSelectedPaymentMethodId(data.stripe_payment_method_id);
      }
    };
    
    loadSavedPaymentMethod();
  }, [job?.id, paymentStatus?.client_setup_complete]);

  // Handle return from Stripe - must be before any early returns to keep hooks order stable
  useEffect(() => {
    const setupStatus = searchParams.get('setup');
    if (setupStatus === 'success') {
      toast({
        title: 'Setup Successful',
        description: 'Your payment method has been verified. Waiting for the other party to complete their setup.',
      });
      
      // Auto-refresh vendor status after returning from Stripe
      if (userRole === 'vendor' && refreshAccountStatus) {
        setTimeout(() => {
          refreshAccountStatus.mutate();
        }, 500);
      }
    } else if (setupStatus === 'cancel') {
      toast({
        title: 'Setup Cancelled',
        description: 'You can retry payment setup at any time by clicking the button below.',
        variant: 'destructive',
      });
    }
  }, [searchParams, toast, userRole, refreshAccountStatus]);

  // Redirect to job detail page if payment processing is disabled —
  // the payment setup flow is gated by the PAYMENT_PROCESSING_ENABLED feature flag.
  useEffect(() => {
    if (!flagLoading && !paymentProcessingEnabled) {
      navigate(`/jobs/${jobId}`);
    }
  }, [flagLoading, paymentProcessingEnabled, navigate, jobId]);

  // Now check if job is loaded AFTER all hooks are called
  if (flagLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center p-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          {jobLoading ? 'Loading job details...' : 'Job not found'}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">Loading payment setup...</div>
      </div>
    );
  }

  const clientComplete = paymentStatus?.client_setup_complete || false;
  const vendorComplete = paymentStatus?.vendor_setup_complete || false;
  const bothComplete = paymentStatus?.both_complete || false;

  const handleClientSetup = () => {
    const successUrl = `${window.location.origin}/jobs/${jobId}/payment-setup?setup=success`;
    const cancelUrl = `${window.location.origin}/jobs/${jobId}/payment-setup?setup=cancel`;
    createPaymentSetup.mutate({ successUrl, cancelUrl });
  };

  const handleVendorSetup = () => {
    if (!user?.email) {
      toast({
        title: 'Error',
        description: 'User email not found. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }
    
    const refreshUrl = `${window.location.origin}/jobs/${jobId}/payment-setup`;
    const returnUrl = `${window.location.origin}/jobs/${jobId}/payment-setup`;
    createConnectAccount.mutate({ 
      email: user.email,
      refreshUrl, 
      returnUrl 
    });
  };

  return (
    <div className="container mx-auto py-8 space-y-6 max-w-5xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/jobs" className="hover:text-foreground">Jobs</Link>
        <span>/</span>
        <Link to={`/jobs/${jobId}`} className="hover:text-foreground">{job.title}</Link>
        <span>/</span>
        <span className="text-foreground">Payment Setup</span>
      </div>

      {/* Header */}
      <div>
        <Link 
          to={`/jobs/${jobId}`} 
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Job
        </Link>
        <h1 className="text-3xl font-bold">Payment Setup Required</h1>
        <p className="text-muted-foreground mt-2">
          {userRole === 'client' 
            ? 'Both parties must complete payment setup before proceeding to milestone planning'
            : 'Connect your Stripe account to receive payment for this job'}
        </p>
      </div>


      {/* Status Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Client Setup Card */}
        <Card className={clientComplete ? 'border-green-200 bg-green-50/50' : ''}>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${clientComplete ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Building className={`h-5 w-5 ${clientComplete ? 'text-green-600' : 'text-gray-600'}`} />
              </div>
              <div>
                <CardTitle className="text-lg">
                  {userRole === 'client' ? 'Your Setup (Client)' : 'Client Setup'}
                </CardTitle>
                <CardDescription>Payment method verification</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              {clientComplete ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-green-900">Payment method ready</p>
                    <p className="text-sm text-green-700 mt-1">
                      Client has verified their payment method
                    </p>
                     {paymentStatus?.client_completed_at && (
                      <p className="text-xs text-green-600 mt-2">
                        Completed {new Date(paymentStatus.client_completed_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </>
              ) : userRole === 'client' && clientComplete && paymentMethods && paymentMethods.length > 1 ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-green-900">Payment method ready</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Choose which payment method to use for this job:
                    </p>
                    <div className="mt-3">
                      <PaymentMethodSelector
                        organizationId={paymentStatus?.client_org_id || ''}
                        selectedPaymentMethodId={selectedPaymentMethodId}
                        onSelect={async (pmId) => {
                          setSelectedPaymentMethodId(pmId);
                          // Save selection to database
                          await supabase
                            .from('payment_setup_status')
                            .update({ stripe_payment_method_id: pmId })
                            .eq('job_id', job.id);
                          toast({
                            title: 'Payment Method Updated',
                            description: 'Your payment method selection has been saved.',
                          });
                        }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    {userRole === 'client' ? (
                      <>
                        <p className="font-medium">Payment method setup</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Verify your payment method to proceed
                        </p>
                        <Button 
                          onClick={handleClientSetup}
                          className="mt-3"
                          disabled={createPaymentSetup.isPending}
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Set Up Now
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="font-medium">Waiting for client</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Client needs to complete payment setup
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          We've notified them
                        </p>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Vendor Setup Card */}
        <Card className={vendorComplete ? 'border-green-200 bg-green-50/50' : ''}>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${vendorComplete ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Building className={`h-5 w-5 ${vendorComplete ? 'text-green-600' : 'text-gray-600'}`} />
              </div>
              <div>
                <CardTitle className="text-lg">
                  {userRole === 'vendor' ? 'Your Setup (Vendor)' : 'Vendor Setup'}
                </CardTitle>
                <CardDescription>Stripe Connect account</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              {vendorComplete ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-green-900">Stripe account connected</p>
                    <p className="text-sm text-green-700 mt-1">
                      Vendor is ready to receive payments
                    </p>
                    {paymentStatus?.vendor_completed_at && (
                      <p className="text-xs text-green-600 mt-2">
                        Completed {new Date(paymentStatus.vendor_completed_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    {userRole === 'vendor' ? (
                      <>
                        <p className="font-medium">Stripe Connect account</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {vendorConnected 
                            ? 'Stripe account connected. Click refresh to sync the status.' 
                            : vendorPending 
                              ? 'Setup in progress. Complete your Stripe onboarding or refresh to check status.' 
                              : 'Connect your Stripe account to receive payment'}
                        </p>
                        {vendorConnected || vendorPending ? (
                          <div className="flex gap-2 mt-3">
                            {!vendorConnected && (
                              <Button 
                                onClick={handleVendorSetup}
                                variant="outline"
                                disabled={createConnectAccount.isPending}
                              >
                                <CreditCard className="h-4 w-4 mr-2" />
                                Continue Setup
                              </Button>
                            )}
                            <Button 
                              onClick={() => refreshAccountStatus.mutate()}
                              variant="secondary"
                              disabled={refreshAccountStatus.isPending}
                            >
                              {refreshAccountStatus.isPending ? 'Refreshing...' : 'Refresh Status'}
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            onClick={handleVendorSetup}
                            className="mt-3"
                            disabled={createConnectAccount.isPending}
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Connect Stripe
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="font-medium">Waiting for vendor</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Vendor needs to complete Stripe setup
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          We've notified them
                        </p>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Completion Status */}
      {bothComplete && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-900 flex items-center justify-between">
            <span>Payment setup complete! You'll be redirected to milestone planning shortly...</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.href = `/jobs/${jobId}/milestones`}
              className="ml-4"
            >
              Go to Milestones
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Return to Job Button */}
      {!bothComplete && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => window.location.href = `/jobs/${jobId}`}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Job Details
          </Button>
        </div>
      )}

      {/* Help Text */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">What happens next?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• Once both parties complete payment setup, you'll automatically proceed to milestone planning</p>
          <p>• The client's payment method will be verified but not charged yet</p>
          <p>• The vendor's Stripe account will be prepared to receive payments</p>
          <p>• Payments will be processed according to the milestone schedule you create together</p>
        </CardContent>
      </Card>
    </div>
  );
}